import React from "react"

import {
  AudioFileTooLargeError,
  uploadAudioFileWithSignedPost,
  useAudioUploadUrlTransport,
} from "@/audio"
import { quotaErrorMessage } from "@/billing"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FileDropZone,
  getUserFacingErrorMessage,
  GroupLabel,
  Label,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@coval/ui"
import { format, isValid, parse } from "date-fns"
import {
  AlertCircle,
  Braces,
  CalendarDays,
  FileText,
  Settings2,
  Upload,
} from "lucide-react"

import { logger } from "@/lib/logger"
import { DateTimePicker } from "@/components/date-time-range-picker/DateTimeRangePicker"
import { LocalWorkerRoutingWarning } from "@/components/LocalWorkerRoutingWarning"

import { useConversationsPopover } from "../contexts/ConversationsPopoverContext"
import {
  ERROR_STEP,
  UPLOAD_STATUS,
  useAudioUploadForm,
} from "../hooks/useAudioUploadForm"
import {
  useSubmitConversationTransport,
  useUploadConversationTracesTransport,
} from "../routes/useConversationUploadTransport"
import {
  getAudioFileExtension,
  parseTranscript,
  validateAudioUrl,
} from "../utils/audioUploadHelpers"
import {
  AudioSourceField,
  type AudioSourceMode,
  type AudioUrlValidationStatus,
} from "./AudioSourceField"
import { MetadataInput } from "./MetadataInput"
import { TracesUploadSection } from "./TracesUploadSection"
import { UploadCompleteView, UploadProgressView } from "./UploadStatusViews"

const log = logger("AudioUploadDropdown")
const LOCAL_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm"

function parseLocalDateTime(value: string): Date | null {
  if (!value) {
    return null
  }
  const parsed = parse(value, LOCAL_DATE_TIME_FORMAT, new Date())
  return isValid(parsed) ? parsed : null
}

interface AudioUploadDropdownProps {
  className?: string
  trigger?: React.ReactElement
  /**
   * How many times the upload has been requested without a click, from the
   * deep link behind the sidebar shortcut. Each new count opens the dialog
   * once, so closing it stays closed until the shortcut is used again.
   */
  openRequestCount?: number
}

interface ConversationAudioSource {
  audioS3Reference?: {
    s3Key: string
    s3Bucket: string
  }
  audioUrl?: string
}

export function AudioUploadDropdown({
  className,
  trigger,
  openRequestCount = 0,
}: AudioUploadDropdownProps) {
  const { isOpen, onOpenChange: setIsOpen } =
    useConversationsPopover("audio-upload")
  const openedRequestRef = React.useRef(0)
  const [audioSourceMode, setAudioSourceMode] =
    React.useState<AudioSourceMode>(null)
  const [audioUrlValidationStatus, setAudioUrlValidationStatus] =
    React.useState<AudioUrlValidationStatus>("idle")
  const [audioUrlValidationError, setAudioUrlValidationError] =
    React.useState("")
  const [isMetadataValid, setIsMetadataValid] = React.useState(true)
  const occurredAtId = React.useId()
  const contentLabelId = React.useId()
  const metadataLabelId = React.useId()
  const dialogTitleRef = React.useRef<HTMLHeadingElement>(null)

  React.useEffect(() => {
    if (
      openRequestCount === 0 ||
      openRequestCount === openedRequestRef.current
    ) {
      return
    }
    openedRequestRef.current = openRequestCount
    setIsOpen(true)
  }, [openRequestCount, setIsOpen])

  const form = useAudioUploadForm()
  const {
    audioFile,
    audioFileError,
    audioUrl,
    setAudioUrl,
    transcriptText,
    transcriptFile,
    transcriptError,
    tracesFile,
    tracesData,
    tracesError,
    tracesWarning,
    setTracesWarning,
    uploadStatus,
    setUploadStatus,
    errorMessage,
    setErrorMessage,
    conversationId,
    setConversationId,
    metadata,
    handleMetadataChange,
    occurredAt,
    setOccurredAt,
    errorStep,
    setErrorStep,
    devId,
    occurredAtISO,
    isUploading,
    isValidForUpload,
    handleAudioFileChange,
    handleTranscriptFileChange,
    handleTracesFileChange,
    resetForm,
  } = form
  const submitConversationMutation = useSubmitConversationTransport()
  const getUploadUrlMutation = useAudioUploadUrlTransport()
  const uploadTracesMutation = useUploadConversationTracesTransport()
  const occurredAtDate = parseLocalDateTime(occurredAt)

  const clearAudioUrlValidation = () => {
    setAudioUrlValidationStatus("idle")
    setAudioUrlValidationError("")
  }
  const resetAudioSource = () => {
    handleAudioFileChange(null)
    setAudioUrl("")
    setAudioSourceMode(null)
    clearAudioUrlValidation()
  }

  const handleSaveAudioUrl = () => {
    const urlToValidate = audioUrl.trim()
    if (!urlToValidate) {
      return
    }

    setAudioUrlValidationError("")

    const result = validateAudioUrl(urlToValidate)
    if (!result.ok) {
      setAudioUrlValidationStatus("error")
      setAudioUrlValidationError(result.error)
      return
    }

    setAudioUrl(urlToValidate)
    setAudioUrlValidationStatus("valid")
  }

  const uploadAudioSource = React.useCallback(
    async (
      file: File | null,
      url: string,
    ): Promise<ConversationAudioSource> => {
      if (file) {
        const {
          url: uploadUrl,
          fields,
          s3Key,
          s3Bucket,
          maxSizeBytes,
        } = await getUploadUrlMutation.mutateAsync({
          fileType: file.type,
          fileExtension: getAudioFileExtension(file.name),
        })

        const response = await uploadAudioFileWithSignedPost({
          url: uploadUrl,
          fields,
          file,
          maxSizeBytes,
        })

        if (!response.ok) {
          const responseBody = await response.text().catch(() => "")
          throw new Error(
            `Audio upload failed (${response.status} ${response.statusText})${
              responseBody ? `: ${responseBody}` : ""
            }`,
          )
        }

        return { audioS3Reference: { s3Key, s3Bucket } }
      }

      return { audioUrl: url.trim() || undefined }
    },
    [getUploadUrlMutation],
  )

  const handleUploadFailure = React.useCallback(
    (
      error: unknown,
      step: (typeof ERROR_STEP)[keyof typeof ERROR_STEP],
      logMessage: string,
      fallbackMessage: string,
    ) => {
      log.error(logMessage, error)
      setUploadStatus(UPLOAD_STATUS.ERROR)
      setErrorStep(step)
      setErrorMessage(
        error instanceof AudioFileTooLargeError
          ? error.message
          : (quotaErrorMessage(error) ??
              getUserFacingErrorMessage(error, { fallback: fallbackMessage })),
      )
    },
    [setUploadStatus, setErrorStep, setErrorMessage],
  )

  const uploadTracesForConversation = async (convId: string) => {
    if (!tracesData) {
      return
    }

    try {
      await uploadTracesMutation.mutateAsync({
        conversationId: convId,
        tracesData,
      })
    } catch (error) {
      const message = getUserFacingErrorMessage(error, {
        fallback: "Failed to upload traces.",
      })
      setTracesWarning(
        `Conversation uploaded successfully, but traces upload failed: ${message}`,
      )
    }
  }

  const handleUpload = async () => {
    const hasTranscript = !!transcriptText.trim()
    const hasAudio = !!audioFile || !!audioUrl.trim()
    if (!hasTranscript && !hasAudio) {
      return
    }
    if (audioUrl.trim() && audioUrlValidationStatus !== "valid") {
      return
    }

    const transcript = hasTranscript
      ? parseTranscript(transcriptText)
      : undefined
    if (hasTranscript && transcript?.length === 0) {
      setUploadStatus(UPLOAD_STATUS.ERROR)
      setErrorStep(ERROR_STEP.PROCESSING)
      setErrorMessage(
        "We couldn't read that transcript. Check the format and try again.",
      )
      return
    }

    let audioSource: ConversationAudioSource = {}
    try {
      setUploadStatus(UPLOAD_STATUS.UPLOADING)
      setErrorMessage("")
      setErrorStep(null)
      if (hasAudio) {
        audioSource = await uploadAudioSource(audioFile, audioUrl)
      }
    } catch (error) {
      handleUploadFailure(
        error,
        ERROR_STEP.UPLOAD,
        "Error uploading conversation audio",
        "Failed to upload the audio. Check the file and try again.",
      )
      return
    }

    try {
      setUploadStatus(UPLOAD_STATUS.PROCESSING)
      const result = await submitConversationMutation.mutateAsync({
        transcript,
        ...audioSource,
        metadata,
        occurred_at: occurredAtISO || undefined,
        dev_id: devId || undefined,
      })

      setConversationId(result.conversationId)
      await uploadTracesForConversation(result.conversationId)
      setUploadStatus(UPLOAD_STATUS.COMPLETE)
    } catch (error) {
      handleUploadFailure(
        error,
        ERROR_STEP.PROCESSING,
        "Error processing uploaded conversation",
        "Failed to process the conversation. Check its content and try again.",
      )
    }
  }

  const resetUploadForm = () => {
    resetForm()
    setAudioSourceMode(null)
    clearAudioUrlValidation()
  }

  const closeDialog = () => {
    resetUploadForm()
    setIsOpen(false)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!isUploading) {
          setIsOpen(open)
          if (!open) {
            resetUploadForm()
          }
        }
      }}
    >
      {trigger ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        <div className="inline-flex">
          <DialogTrigger asChild>
            <Button
              variant="secondary"
              icon
              tooltip="Upload conversation"
              className={className}
            >
              <Upload className="size-4" />
            </Button>
          </DialogTrigger>
        </div>
      )}

      <DialogContent
        size="lg"
        className="w-[calc(100vw-2rem)] max-w-3xl overflow-visible"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          dialogTitleRef.current?.focus()
        }}
      >
        <DialogHeader className="px-6 pt-6 pb-5">
          <DialogTitle
            ref={dialogTitleRef}
            tabIndex={-1}
            className="text-base outline-none"
          >
            Upload Conversation
          </DialogTitle>
          <DialogDescription className="sr-only">
            Upload audio, a transcript, or both as one conversation.
          </DialogDescription>
        </DialogHeader>

        {uploadStatus === UPLOAD_STATUS.IDLE ||
        uploadStatus === UPLOAD_STATUS.ERROR ? (
          <>
            <DialogBody className="space-y-6 overflow-visible border-y border-coval-border px-6 py-6">
              <div>
                <GroupLabel
                  id={contentLabelId}
                  tooltip="Provide audio, a transcript, or both. At least one source is required."
                >
                  Conversation content
                </GroupLabel>
              </div>

              <div
                role="group"
                aria-labelledby={contentLabelId}
                className="grid gap-4 md:grid-cols-2"
              >
                <AudioSourceField
                  audioFile={audioFile}
                  audioFileError={audioFileError}
                  audioUrl={audioUrl}
                  disabled={isUploading}
                  mode={audioSourceMode}
                  validationError={audioUrlValidationError}
                  validationStatus={audioUrlValidationStatus}
                  onFileChange={(file) => {
                    handleAudioFileChange(file)
                    if (file) {
                      setAudioUrl("")
                      clearAudioUrlValidation()
                    }
                  }}
                  onModeChange={setAudioSourceMode}
                  onReset={resetAudioSource}
                  onSaveUrl={handleSaveAudioUrl}
                  onUrlChange={(url) => {
                    setAudioUrl(url)
                    clearAudioUrlValidation()
                  }}
                />

                <section className="space-y-4 rounded-md border border-coval-border bg-surface-0 p-5">
                  <div className="space-y-1">
                    <GroupLabel>
                      <span className="flex items-center gap-2">
                        <FileText className="size-4 text-coval-text-muted" />
                        Transcript
                      </span>
                    </GroupLabel>
                    <p className="text-xs text-coval-text-muted">TXT or JSON</p>
                  </div>
                  <div className="space-y-2">
                    <FileDropZone
                      file={transcriptFile}
                      accept=".txt,.json"
                      label="Conversation transcript file"
                      fileIcon={<FileText />}
                      showChangeButton={false}
                      disabled={isUploading}
                      className="py-5"
                      onFileChange={(file) => {
                        void handleTranscriptFileChange(file)
                      }}
                    />
                    {transcriptError && (
                      <p
                        role="alert"
                        className="text-xs text-coval-danger-text"
                      >
                        {transcriptError}
                      </p>
                    )}
                  </div>
                </section>
              </div>

              <Accordion
                type="single"
                collapsible
                className="rounded-md border border-coval-border bg-surface-default"
              >
                <AccordionItem value="additional" className="border-b-0">
                  <AccordionTrigger className="px-5 py-4 text-sm font-semibold">
                    <span className="flex items-center gap-2">
                      <Settings2 className="size-4" />
                      Advanced
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5">
                    <div className="space-y-6 pt-2">
                      <section className="space-y-4 rounded-md border border-coval-border bg-surface-0 p-5">
                        <div className="space-y-1">
                          <GroupLabel>
                            <span className="flex items-center gap-2">
                              <Braces className="size-4 text-coval-text-muted" />
                              OpenTelemetry Traces
                            </span>
                          </GroupLabel>
                          <p className="text-xs text-coval-text-muted">JSON</p>
                        </div>
                        <TracesUploadSection
                          tracesFile={tracesFile}
                          onTracesFileChange={(file) => {
                            void handleTracesFileChange(file)
                          }}
                          tracesError={tracesError}
                        />
                      </section>

                      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <Label
                          htmlFor={occurredAtId}
                          tooltip="When the conversation happened, in your local time. Leave blank to use the upload time."
                        >
                          Occurred at
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              id={occurredAtId}
                              type="button"
                              variant="secondary"
                              aria-label="Conversation occurred at"
                              className="w-full justify-between px-2 font-normal sm:w-[13.5rem]"
                            >
                              <span
                                className={
                                  occurredAtDate
                                    ? "truncate"
                                    : "truncate text-coval-text-muted"
                                }
                              >
                                {occurredAtDate
                                  ? format(occurredAtDate, "MM/dd/yyyy, h:mm a")
                                  : "mm/dd/yyyy, --:-- --"}
                              </span>
                              <CalendarDays className="shrink-0" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-auto p-4">
                            <DateTimePicker
                              value={occurredAtDate}
                              onChange={(value) =>
                                setOccurredAt(
                                  value
                                    ? format(value, LOCAL_DATE_TIME_FORMAT)
                                    : "",
                                )
                              }
                              hideTimezoneHint
                            />
                            <div className="mt-4 flex items-center justify-between">
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setOccurredAt("")}
                              >
                                Clear
                              </Button>
                              <PopoverClose asChild>
                                <Button type="button" variant="secondary">
                                  Done
                                </Button>
                              </PopoverClose>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </section>

                      <section
                        role="group"
                        aria-labelledby={metadataLabelId}
                        className="space-y-3"
                      >
                        <GroupLabel
                          id={metadataLabelId}
                          tooltip="Add searchable context to the conversation as key-value pairs."
                        >
                          Metadata
                        </GroupLabel>
                        <MetadataInput
                          metadata={metadata}
                          onChange={handleMetadataChange}
                          onValidityChange={setIsMetadataValid}
                        />
                      </section>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <LocalWorkerRoutingWarning />

              {uploadStatus === UPLOAD_STATUS.ERROR && (
                <div className="rounded-md border border-coval-danger-border bg-coval-danger-surface p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 size-5 shrink-0 text-coval-danger-on-surface" />
                    <div className="flex-1">
                      <div className="mb-1 text-sm font-medium text-coval-danger-on-surface">
                        Upload failed
                      </div>
                      <div className="text-sm text-coval-danger-on-surface">
                        {errorMessage}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogBody>

            <DialogFooter className="px-6 pt-5 pb-6">
              {uploadStatus === UPLOAD_STATUS.ERROR && (
                <Button type="button" variant="ghost" onClick={resetUploadForm}>
                  Reset
                </Button>
              )}
              <Button type="button" variant="secondary" onClick={closeDialog}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  void handleUpload()
                }}
                disabled={
                  !isValidForUpload ||
                  !isMetadataValid ||
                  (!!audioUrl.trim() && audioUrlValidationStatus !== "valid")
                }
                aria-label="Upload"
              >
                {uploadStatus === UPLOAD_STATUS.ERROR &&
                errorStep === ERROR_STEP.PROCESSING
                  ? "Retry"
                  : "Upload conversation"}
              </Button>
            </DialogFooter>
          </>
        ) : uploadStatus === UPLOAD_STATUS.UPLOADING ||
          uploadStatus === UPLOAD_STATUS.PROCESSING ? (
          <DialogBody className="border-t border-coval-border">
            <UploadProgressView
              isUploading={uploadStatus === UPLOAD_STATUS.UPLOADING}
            />
          </DialogBody>
        ) : uploadStatus === UPLOAD_STATUS.COMPLETE ? (
          <>
            <DialogBody className="border-y border-coval-border">
              <UploadCompleteView
                conversationId={conversationId}
                tracesWarning={tracesWarning}
              />
            </DialogBody>
            <DialogFooter className="px-6 pt-5 pb-6">
              <Button type="button" onClick={closeDialog}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
